import { useEffect, useId, useState } from "react";
import type { LeadRow } from "@gioia365/contracts";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { BookAppointmentDialog, type AppointmentKind } from "../appointments/BookAppointmentDialog";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { Reason } from "../design/components/Reason";
import { Segmented } from "../design/components/Segmented";
import { TextField } from "../design/components/TextField";
import { messageForError } from "../i18n/errors";
import { useT } from "../i18n/locale";
import { SEARCH_MAX_LENGTH } from "../leads/lead-list-hooks";
import { leadListQuery, useLeadView } from "../leads/lead-queries";
import type { TabContext } from "../session/tab-context";
import "./opportunities.css";

// «Nuova opportunità» dalla pagina «Opportunità» (Lucio, 29/10/2026: «devo poter creare nuove opportunità direttamente da lì»).
// Un'Opportunità nasce con la prima disco o demo prenotata (SEZ-9, R7): la finestra cerca il Lead (la ricerca dell'elenco dei Lead,
// nel perimetro di chi guarda), fa scegliere disco o demo e apre «Prenota disco o demo» su quel Lead. La prenotazione rilegge le
// Opportunità (`useAppointmentWrite`); il server decide ogni permesso e rifiuta, con il suo motivo, un Lead con un appuntamento già
// aperto o che chi guarda non può cambiare.

const RESULTS = 8;
const MIN_CHARS = 2;

type Props = { tab: TabContext; timeZone: string; onClose: () => void };

export function leadLabelOf(lead: Pick<LeadRow, "displayName" | "salonName">, fallback: string): string {
  return [lead.displayName, lead.salonName].filter((part): part is string => part !== null && part !== "").join(" · ") || fallback;
}

export function NewOpportunityDialog({ tab, timeZone, onClose }: Props) {
  const t = useT();
  const view = useLeadView();
  const ids = useId();
  const [kind, setKind] = useState<AppointmentKind>("disco");
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [chosen, setChosen] = useState<string | null>(null);

  // La ricerca parte dopo una breve pausa nella scrittura, come nell'elenco dei Lead.
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(text.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [text]);

  const enabled = q.length >= MIN_CHARS;
  const results = useQuery({
    ...leadListQuery(tab, { q, sort: "createdAt", direction: "desc", offset: 0, limit: RESULTS }, view),
    enabled,
    placeholderData: keepPreviousData,
  });

  if (chosen !== null) return <BookAppointmentDialog tab={tab} leadId={chosen} timeZone={timeZone} initialKind={kind} onClose={onClose} />;

  const rows = enabled ? (results.data?.rows ?? []) : [];
  let list;
  if (!enabled) list = <p className="g-new-opportunity__hint">{t("opportunities.newDialog.typeMore")}</p>;
  else if (results.isError) list = <Reason tone="warning" role="alert">{messageForError(t, results.error)}</Reason>;
  else if (results.isPending) list = <p className="g-new-opportunity__hint" role="status">{t("common.loading")}</p>;
  else if (rows.length === 0) list = <p className="g-new-opportunity__hint" role="status">{t("opportunities.newDialog.noLead")}</p>;
  else
    list = (
      <ul className="g-new-opportunity__results" aria-label={t("opportunities.newDialog.results")}>
        {rows.map((lead) => (
          <li key={lead.id}>
            <button type="button" className="g-new-opportunity__lead" onClick={() => setChosen(lead.id)}>
              <strong>{leadLabelOf(lead, t("opportunities.newDialog.unnamed"))}</strong>
              <span>{[lead.city?.name ?? null, lead.status.name].filter(Boolean).join(" · ")}</span>
            </button>
          </li>
        ))}
      </ul>
    );

  return (
    <Dialog open title={t("opportunities.newDialog.title")} onClose={onClose}>
      <div className="g-new-opportunity">
        <p className="g-new-opportunity__intro">{t("opportunities.newDialog.intro")}</p>
        <Segmented
          label={t("opportunities.newDialog.kind")}
          options={[
            { value: "disco", label: t("appointments.kind.disco") },
            { value: "demo", label: t("appointments.kind.demo") },
          ]}
          value={kind}
          onChange={(value) => setKind(value === "demo" ? "demo" : "disco")}
        />
        <TextField
          id={`${ids}-lead`}
          label={t("opportunities.newDialog.search")}
          type="search"
          autoComplete="off"
          maxLength={SEARCH_MAX_LENGTH}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        {list}
        <div className="g-sheet__actions">
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
